import React, { useMemo, useState } from "react"
import { Switch, useHistory, useLocation } from "react-router-dom"
import { Box, Button, Divider, makeStyles, Tab, Tabs } from "@material-ui/core"
import { AddLocationOutlined } from "@material-ui/icons"
import Config from "react-global-configuration"
import InfiniteScroll from "react-infinite-scroll-component"
import {
  LocationActionCard,
  RequirePermissions,
  NoItemsMessage,
  SearchInput,
  LoadingSpinner,
  Icon,
  PrivateRoute,
} from "../../components"
import { useAuth } from "../../services"
import { LocationCreator } from "../../components/LocationCreator/LocationCreator"
import SettingsFooter from "../../components/Settings/SettingsFooter"
import { joinStringsWithNaturalLanguage, toId } from "../../utils"
import { useQueryRegions } from "../../data/locations/useQueryRegions"
import RegionActionCard from "../../components/ActionCards/RegionActionCard"
import RegionCreator from "../../components/LocationCreator/RegionCreator"

const initialLimit = 8
const incrementLimit = 4

const useStyles = makeStyles(() => ({
  infiniteScrollWrapper: {
    "&&>div": {
      display: "contents",
    },
  },
  infiniteScroll: {
    display: "contents",
  },
}))

const Locations = () => {
  const classes = useStyles()
  const { clientKiosk: kiosk } = Config.get()
  const {
    settings: { locations },
    location,
    setLocation,
  } = useAuth()
  const [open, setOpen] = useState(false)
  const [edit, setEdit] = useState(null)
  const [limit, setLimit] = useState(initialLimit)
  const [searchText, setSearchText] = useState("")

  const handleFilter = (text) => {
    setSearchText(text.toLowerCase())
  }

  const handleCreateLocation = () => {
    setOpen(true)
  }
  const handleCreateLocationClose = () => {
    setOpen(false)
    setEdit(null)
  }
  const handleEdit = (editLocation) => {
    setEdit(editLocation)
    setOpen(true)
  }
  const handleDelete = () => {}

  const items = useMemo(() => {
    const showLocations = locations.filter((item) => !searchText || item.name.toLowerCase().includes(searchText))

    const visibleLocations = showLocations.slice(0, limit)

    if (!showLocations.length) {
      return <NoItemsMessage>No locations</NoItemsMessage>
    }

    return (
      <InfiniteScroll
        dataLength={visibleLocations.length}
        next={() => setLimit(limit + incrementLimit)}
        hasMore={visibleLocations.length < showLocations.length}
        className={classes.infiniteScroll}
      >
        {visibleLocations.map((item) => {
          const active = toId(item) === toId(location)
          return (
            <LocationActionCard
              key={toId(item)}
              location={item}
              detail={active && "Your current active location"}
              actionType={kiosk ? null : "radio"}
              checked={active}
              onChange={() => setLocation(item)}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )
        })}
      </InfiniteScroll>
    )
  }, [classes.infiniteScroll, kiosk, limit, location, locations, searchText, setLocation])

  if (!locations.length) return <NoItemsMessage>No locations are configured for your user</NoItemsMessage>

  const hasFilter = locations.length > 5

  return (
    <>
      <LocationCreator open={open} onClose={handleCreateLocationClose} edit={edit} />

      {hasFilter && <SearchInput placeholder="Filter" boxProps={{ pl: 0, pr: 0 }} onChange={handleFilter} />}

      <Box mb={5} data-cy="Locations-list">
        {items}
      </Box>

      <RequirePermissions requires="location_create">
        <SettingsFooter>
          <Button variant="contained" color="primary" onClick={handleCreateLocation} data-cy="Button-create-location">
            <AddLocationOutlined /> <Box ml={1}>Add location</Box>
          </Button>
        </SettingsFooter>
      </RequirePermissions>
    </>
  )
}

const Regions = () => {
  const { data, loading, refetch } = useQueryRegions()
  const {
    settings: { locations },
  } = useAuth()
  const [open, setOpen] = useState(false)
  const [edit, setEdit] = useState(null)

  const handleCreate = () => {
    setOpen(true)
  }

  const handleEdit = (region) => {
    setEdit(region)
    setOpen(true)
  }

  const handleClose = async () => {
    setOpen(false)
    setEdit(null)
    await refetch()
  }

  const handleDelete = () => {}

  const handleFilter = () => {}

  const hasFilter = false

  const regions = data?.regions || []

  return (
    <>
      <RegionCreator open={open} onClose={handleClose} edit={edit} />

      {hasFilter && <SearchInput placeholder="Filter" boxProps={{ pl: 0, pr: 0 }} onChange={handleFilter} />}

      <Box mb={5} data-cy="Triggers-list">
        {regions.map((region) => {
          const regionLocations = locations.filter(
            (location) => location.region && toId(location.region) === toId(region)
          )

          return (
            <RegionActionCard
              key={toId(region)}
              region={region}
              detail={
                <Box mr={1}>
                  {regionLocations.length
                    ? joinStringsWithNaturalLanguage(regionLocations.map((location) => location.name))
                    : "No locations"}
                </Box>
              }
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          )
        })}

        {!data && loading && (
          <Box display="flex" justifyContent="center">
            <LoadingSpinner size={60} />
          </Box>
        )}

        {regions.length === 0 && <NoItemsMessage>No regions created</NoItemsMessage>}
      </Box>

      <RequirePermissions requires="location_create">
        <SettingsFooter>
          <Button variant="contained" color="primary" onClick={handleCreate} data-cy="Button-create-region">
            <Icon name="region" /> <Box ml={1}>Create region</Box>
          </Button>
        </SettingsFooter>
      </RequirePermissions>
    </>
  )
}

const Index = () => {
  const location = useLocation()
  const history = useHistory()
  const { hasFeature } = useAuth()

  const handleTabChange = (event, value) => {
    history.push(`/account/${value}`)
  }

  const path = location.pathname.split("/")
  const page = path.length > 2 ? path[2] : "locations"

  const hasRegions = hasFeature("regions")

  return (
    <>
      {hasRegions && (
        <Tabs value={page} onChange={handleTabChange} data-cy="Tabs">
          <Tab label="Locations" value="locations" data-cy="Tab-locations" />
          <Tab label="Regions" value="regions" data-cy="Tab-regions" />
        </Tabs>
      )}

      {hasRegions && <Divider />}

      <Box pt={hasRegions ? 5 : 0}>
        <Switch>
          <PrivateRoute path="/account/locations" component={Locations} />
          <PrivateRoute path="/account/regions" component={Regions} feature="regions" />
        </Switch>
      </Box>
    </>
  )
}

export default Index
